import { timestampToISO, timestampToDateString } from './timestamp';

function str(value) {
    return value != null ? String(value) : '';
}

function parseJsonField(raw) {
    const text = str(raw).trim();
    if (!text) {
        return null;
    }
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

/** started_at → getStartedAt */
function protoTimestampGetterName(field) {
    return `get${field.split('_').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('')}`;
}

/** Lê Timestamp do proto e grava RFC3339 UTC no objeto REST. */
function assignTimestampsFromProto(out, proto, fields) {
    const result = { ...out };
    if (!proto) {
        return result;
    }
    fields.forEach((field) => {
        const getter = protoTimestampGetterName(field);
        if (typeof proto[getter] !== 'function') {
            return;
        }
        const iso = timestampToISO(proto[getter]());
        if (iso) {
            result[field] = iso;
        }
    });
    return result;
}

export function paginationFromProto(meta) {
    if (!meta) {
        return {
            page: 1,
            limit: 10,
            total: 0,
            total_pages: 1,
        };
    }
    return {
        page: meta.getPage() || 1,
        limit: meta.getLimit() || 10,
        total: meta.getTotal() || 0,
        total_pages: meta.getTotalPages() || 1,
    };
}

/** List with top-level array + pagination (customers, printers, products, …). */
export function paginatedListResponse(listKey, items, meta) {
    return {
        [listKey]: items,
        ...paginationFromProto(meta),
    };
}

/** List nested under `data` (jobs, transactions, printer-maintenances). */
export function nestedListResponse(listKey, items, meta) {
    return {
        success: true,
        data: {
            [listKey]: items,
            ...paginationFromProto(meta),
        },
    };
}

export function customerRefFromProto(ref) {
    if (!ref) {
        return null;
    }
    const out = {
        name: ref.getName() || '',
        category: ref.getCategory?.() || 'cliente',
    };
    if (ref.getId()) {
        out.id = ref.getId();
        out.uuid = ref.getId();
    }
    if (ref.getPhone()) {
        out.phone = ref.getPhone();
    }
    if (ref.getEmail()) {
        out.email = ref.getEmail();
    }
    if (ref.getDocument()) {
        out.document = ref.getDocument();
    }
    if (typeof ref.getAddress === 'function' && ref.getAddress()) {
        out.address = ref.getAddress();
    }
    if (typeof ref.getAddressNumber === 'function' && ref.getAddressNumber()) {
        out.address_number = ref.getAddressNumber();
    }
    if (typeof ref.getAddressComplement === 'function' && ref.getAddressComplement()) {
        out.address_complement = ref.getAddressComplement();
    }
    if (typeof ref.getNeighborhood === 'function' && ref.getNeighborhood()) {
        out.neighborhood = ref.getNeighborhood();
    }
    if (typeof ref.getCity === 'function' && ref.getCity()) {
        out.city = ref.getCity();
    }
    if (typeof ref.getState === 'function' && ref.getState()) {
        out.state = ref.getState();
    }
    if (typeof ref.getZipCode === 'function' && ref.getZipCode()) {
        out.zip_code = ref.getZipCode();
    }
    return out;
}

export function salesChannelRefFromProto(ref) {
    if (!ref) {
        return null;
    }
    const out = {};
    if (ref.getName()) {
        out.name = ref.getName();
    }
    if (typeof ref.getId === 'function' && ref.getId()) {
        out.id = ref.getId();
    }
    if (typeof ref.getKind === 'function' && ref.getKind()) {
        out.kind = ref.getKind();
    }
    if (typeof ref.getHoldsPayout === 'function') {
        out.holds_payout = Boolean(ref.getHoldsPayout());
    }
    return Object.keys(out).length > 0 ? out : null;
}

export function shippingMethodRefFromProto(ref) {
    if (!ref) {
        return null;
    }
    const out = {};
    if (ref.getName()) {
        out.name = ref.getName();
    }
    if (ref.getCost()) {
        out.cost = ref.getCost();
    }
    if (ref.getDeliveryDays()) {
        out.delivery_days = ref.getDeliveryDays();
    }
    if (ref.getDeadline()) {
        out.deadline = ref.getDeadline();
    }
    return out;
}

export function checkoutProductFromProto(p) {
    if (!p) {
        return null;
    }
    const id = p.getId() || '';
    const out = { quantity: p.getQuantity() || 0 };
    if (id) {
        out.id = id;
        out.uuid = id;
    }
    if (p.getName()) {
        out.name = p.getName();
    }
    if (p.getBasePrice()) {
        out.base_price = p.getBasePrice();
    }
    if (p.getCostPrice()) {
        out.cost_price = p.getCostPrice();
    }
    if (typeof p.getKind === 'function' && p.getKind()) {
        out.kind = p.getKind();
    }
    return out;
}

export function paymentMethodFromProto(p) {
    if (!p) {
        return null;
    }
    const out = {
        name: p.getName() || '',
        installments: p.getInstallments() || 0,
        amount: p.getAmount() || 0,
    };
    if (typeof p.getPaymentDate === 'function') {
        const paymentDate = timestampToISO(p.getPaymentDate());
        if (paymentDate) {
            out.payment_date = paymentDate;
        }
    }
    return out;
}

export function paymentDetailsFromProto(p) {
    if (!p) {
        return null;
    }
    const out = { paid: Boolean(p.getPaid()) };
    const paymentDate = timestampToISO(p.getPaymentDate());
    if (paymentDate) {
        out.payment_date = paymentDate;
    }
    return out;
}

export function userFromProto(u) {
    if (!u) {
        return null;
    }
    const out = {
        uuid: u.getUuid() || '',
        name: u.getName() || '',
        email: u.getEmail() || '',
        is_master: typeof u.getIsMaster === 'function' ? Boolean(u.getIsMaster()) : false,
    };
    if (u.getProfilePicture()) {
        out.profile_picture = u.getProfilePicture();
    }
    if (u.getWhatsapp()) {
        out.whatsapp = u.getWhatsapp();
    }
    if (u.getSite()) {
        out.site = u.getSite();
    }
    if (u.getInstagram()) {
        out.instagram = u.getInstagram();
    }
    if (u.getTiktok()) {
        out.tiktok = u.getTiktok();
    }
    if (u.getStoreName()) {
        out.store_name = u.getStoreName();
    }
    if (typeof u.getAddress === 'function' && u.getAddress()) {
        out.address = u.getAddress();
    }
    if (typeof u.getAddressNumber === 'function' && u.getAddressNumber()) {
        out.address_number = u.getAddressNumber();
    }
    if (typeof u.getAddressComplement === 'function' && u.getAddressComplement()) {
        out.address_complement = u.getAddressComplement();
    }
    if (typeof u.getNeighborhood === 'function' && u.getNeighborhood()) {
        out.neighborhood = u.getNeighborhood();
    }
    if (typeof u.getZipCode === 'function' && u.getZipCode()) {
        out.zip_code = u.getZipCode();
    }
    if (typeof u.getCity === 'function' && u.getCity()) {
        out.city = u.getCity();
    }
    if (typeof u.getState === 'function' && u.getState()) {
        out.state = u.getState();
    }
    if (typeof u.getHasCoordinates === 'function' && u.getHasCoordinates()) {
        out.latitude = u.getLatitude();
        out.longitude = u.getLongitude();
    }
    const createdAt = timestampToISO(u.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    const updatedAt = timestampToISO(u.getUpdatedAt());
    if (updatedAt) {
        out.updated_at = updatedAt;
    }
    return out;
}

export function adminUserFromProto(u) {
    if (!u) {
        return null;
    }
    const out = {
        uuid: u.getUuid() || '',
        name: u.getName() || '',
        email: u.getEmail() || '',
        store_name: u.getStoreName() || '',
        plan_name: u.getPlanName() || 'Free',
        plan_migration_pending: Boolean(u.getPlanMigrationPending()),
        active: u.getActive() !== false,
        is_master: Boolean(u.getIsMaster()),
        printers_count: Number(u.getPrintersCount() || 0),
        jobs_count: Number(u.getJobsCount() || 0),
        products_count: Number(u.getProductsCount() || 0),
        subscription_uuid: typeof u.getSubscriptionUuid === 'function' ? (u.getSubscriptionUuid() || '') : '',
        subscription_active: typeof u.getSubscriptionActive === 'function' ? u.getSubscriptionActive() !== false : true,
        mp_preapproval_id: typeof u.getMpPreapprovalId === 'function' ? (u.getMpPreapprovalId() || '') : '',
        mp_subscription_status: typeof u.getMpSubscriptionStatus === 'function' ? (u.getMpSubscriptionStatus() || '') : '',
        mp_trial_active: typeof u.getMpTrialActive === 'function' ? Boolean(u.getMpTrialActive()) : false,
    };
    if (out.plan_migration_pending && u.getPendingPlanName()) {
        out.pending_plan_name = u.getPendingPlanName();
    }
    const lastLoginAt = timestampToISO(u.getLastLoginAt?.());
    if (lastLoginAt) {
        out.last_login_at = lastLoginAt;
    }
    const createdAt = timestampToISO(u.getCreatedAt?.());
    if (createdAt) {
        out.created_at = createdAt;
    }
    const updatedAt = timestampToISO(u.getUpdatedAt?.());
    if (updatedAt) {
        out.updated_at = updatedAt;
    }
    const graceUntil = timestampToISO(u.getMpGraceUntil?.());
    if (graceUntil) {
        out.mp_grace_until = graceUntil;
    }
    return out;
}

export function adminUserStatsFromProto(stats) {
    if (!stats) {
        return {
            total: 0,
            active_count: 0,
            inactive_count: 0,
            free_count: 0,
            growing_count: 0,
            pro_count: 0,
        };
    }
    return {
        total: Number(stats.getTotal() || 0),
        active_count: Number(stats.getActiveCount() || 0),
        inactive_count: Number(stats.getInactiveCount() || 0),
        free_count: Number(stats.getFreeCount() || 0),
        growing_count: Number(stats.getGrowingCount() || 0),
        pro_count: Number(stats.getProCount() || 0),
    };
}

export function adminUsersListResponseFromProto(res) {
    const meta = paginationFromProto(res?.getMeta?.());
    return {
        data: (res?.getDataList?.() || []).map(adminUserFromProto).filter(Boolean),
        ...meta,
        stats: adminUserStatsFromProto(res?.getStats?.()),
    };
}

export function integrationsResponseFromProto(res) {
    if (!res) {
        return { data: { has_pro_access: false, plan_name: '', integration_key: '' } };
    }
    const data = {
        has_pro_access: Boolean(res.getHasProAccess?.()),
        plan_name: res.getPlanName?.() || '',
    };
    const key = res.getIntegrationKey?.() || '';
    if (key) {
        data.integration_key = key;
    }
    return { data };
}

export function loginResponseFromProto(res) {
    if (!res) {
        return {};
    }
    return authSessionResponseFromProto(res);
}

export function registerResponseFromProto(res) {
    const base = authSessionResponseFromProto(res);
    return {
        ...base,
        message: res?.getMessage?.() || '',
    };
}

export function authSessionResponseFromProto(res) {
    if (!res) {
        return {};
    }
    return {
        token_type: res.getTokenType() || 'Bearer',
        access_token: res.getAccessToken() || '',
        user: userFromProto(res.getUser()),
        plan: planInfoFromProto(res.getPlan?.()),
    };
}

export function planInfoFromProto(plan) {
    if (!plan) {
        return null;
    }
    return {
        name: plan.getName() || '',
        subscription_price: plan.getSubscriptionPrice() || 0,
    };
}

export function accountResponseFromProto(res) {
    return {
        data: {
            user: userFromProto(res?.getUser()),
            plan: planInfoFromProto(res?.getPlan()),
        },
    };
}

export function updateProfileResponseFromProto(res) {
    return {
        data: userFromProto(res?.getData()),
    };
}

export function updatePasswordResponseFromProto(res) {
    return {
        data: {
            updated: Boolean(res?.getUpdated()),
        },
    };
}

export function updateAvatarResponseFromProto(res) {
    return {
        data: {
            image_url: res?.getImageUrl() || '',
        },
    };
}

export function printerListItemFromProto(p) {
    if (!p) {
        return null;
    }
    return {
        id: p.getId() || '',
        name: p.getName() || '',
        brand: p.getBrand() || '',
        model: p.getModel() || '',
        printer_model_uuid: typeof p.getPrinterModelUuid === 'function' ? p.getPrinterModelUuid() : '',
        avg_consumption_watts: typeof p.getAvgConsumptionWatts === 'function' ? p.getAvgConsumptionWatts() : 0,
        consumption: p.getConsumption() || 0,
        price: p.getPrice() || 0,
        active: p.getActive(),
        filaments: printerFilamentAssignmentsFromProto(p),
    };
}

export function printerFilamentAssignmentFromProto(a) {
    if (!a) {
        return null;
    }
    return {
        uuid: a.getUuid?.() || '',
        filament_uuid: a.getFilamentUuid?.() || '',
        location: a.getLocation?.() || '',
        sort_order: a.getSortOrder?.() || 0,
        type: a.getType?.() || '',
        color: a.getColor?.() || '',
        color_label: a.getColorLabel?.() || '',
        brand: a.getBrand?.() || '',
        printer_uuid: a.getPrinterUuid?.() || '',
        printer_name: a.getPrinterName?.() || '',
    };
}

function printerFilamentAssignmentsFromProto(p) {
    if (!p || typeof p.getFilamentsList !== 'function') {
        return [];
    }
    return (p.getFilamentsList() || []).map(printerFilamentAssignmentFromProto).filter(Boolean);
}

export function printerFromProto(p) {
    if (!p) {
        return null;
    }
    const out = {
        uuid: p.getUuid() || '',
        name: p.getName() || '',
        brand: p.getBrand() || '',
        model: p.getModel() || '',
        printer_model_uuid: typeof p.getPrinterModelUuid === 'function' ? p.getPrinterModelUuid() : '',
        avg_consumption_watts: typeof p.getAvgConsumptionWatts === 'function' ? p.getAvgConsumptionWatts() : 0,
        power_consumption_watts: p.getPowerConsumptionWatts() || 0,
        acquisition_value: p.getAcquisitionValue() || 0,
        hourly_printing_cost: p.getHourlyPrintingCost() || 0,
        consumption: p.getPowerConsumptionWatts() || 0,
        price: p.getAcquisitionValue() || 0,
        active: p.getActive(),
        filaments: printerFilamentAssignmentsFromProto(p),
    };
    const acquisitionDate = timestampToDateString(p.getAcquisitionDate());
    if (acquisitionDate) {
        out.acquisition_date = acquisitionDate;
    }
    return assignTimestampsFromProto(out, p, ['created_at', 'updated_at']);
}

export function filamentListItemFromProto(m) {
    if (!m) {
        return null;
    }
    const out = {
        id: m.getId() || '',
        type: m.getType() || '',
        color: m.getColor() || '',
        color_label: m.getColorLabel() || '',
        brand: m.getBrand() || '',
        spool_weight: m.getSpoolWeight() || 0,
        current_weight: m.getCurrentWeight() || 0,
        cost: m.getCost() || 0,
        cost_per_kilogram: m.getCost() || 0,
        usage_count: m.getUsageCount() || 0,
        archived_at: timestampToISO(m.getArchivedAt()) || null,
        printers: typeof m.getPrintersList === 'function'
            ? (m.getPrintersList() || []).map((link) => ({
                uuid: link.getUuid?.() || '',
                name: link.getName?.() || '',
                assignment_uuid: link.getAssignmentUuid?.() || '',
                location: link.getLocation?.() || '',
                sort_order: link.getSortOrder?.() || 0,
            }))
            : [],
    };
    const createdAt = timestampToISO(m.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    return out;
}

export function filamentFromProto(m) {
    if (!m) {
        return null;
    }
    const out = {
        uuid: m.getUuid() || '',
        type: m.getType() || '',
        color: m.getColor() || '',
        color_label: m.getColorLabel() || '',
        brand: m.getBrand() || '',
        spool_weight: m.getSpoolWeight() || 0,
        current_weight: m.getCurrentWeight() || 0,
        cost: m.getCost() || 0,
        cost_per_kilogram: m.getCost() || 0,
        usage_count: m.getUsageCount() || 0,
        archived_at: timestampToISO(m.getArchivedAt()) || null,
    };
    return assignTimestampsFromProto(out, m, ['created_at', 'updated_at']);
}

export function filamentUsageFromProto(u) {
    if (!u) {
        return null;
    }
    const out = {
        uuid: u.getUuid() || '',
        filament_uuid: u.getFilamentUuid() || '',
        job_uuid: u.getJobUuid() || '',
        grams: u.getGrams() || 0,
        cost_per_kilogram: u.getCostPerKilogram() || 0,
        job_file_name: u.getJobFileName() || '',
        job_status: u.getJobStatus() || '',
        printer_name: u.getPrinterName() || '',
    };
    return assignTimestampsFromProto(out, u, ['job_started_at', 'job_finished_at', 'created_at']);
}

export function replacementPartItemFromProto(item) {
    if (!item) {
        return null;
    }
    const uuid = item.getUuid() || item.getId() || '';
    const out = {
        id: uuid,
        uuid,
        name: item.getName() || '',
        unit_value_cents: item.getUnitValueCents() || 0,
        quantity: item.getQuantity() || 0,
        stock_value_cents: item.getStockValueCents() || 0,
        printer_brand: item.getPrinterBrand() || '',
        printer_model: item.getPrinterModel() || '',
        applies_to_all_printers: item.getAppliesToAllPrinters(),
        usages: (typeof item.getUsagesList === 'function' ? item.getUsagesList() : []).map(stockUsageFromProto).filter(Boolean),
    };
    return assignTimestampsFromProto(out, item, ['created_at', 'updated_at']);
}

function stockUsageFromProto(usage) {
    if (!usage) {
        return null;
    }
    const out = {
        quantity: usage.getQuantity() || 1,
        printer_name: usage.getPrinterName() || '',
        maintenance_uuid: usage.getMaintenanceUuid() || '',
    };
    return assignTimestampsFromProto(out, usage, ['used_at']);
}

function stockExtremeFromProto(item) {
    if (!item) {
        return null;
    }
    return {
        name: item.getName() || '',
        quantity: item.getQuantity() || 0,
    };
}

export function replacementPartsStatsFromProto(stats) {
    if (!stats) {
        return {
            total_items: 0,
            total_stock_value_cents: 0,
            highest_stock: [],
            lowest_stock: [],
        };
    }
    return {
        total_items: stats.getTotalItems() || 0,
        total_stock_value_cents: stats.getTotalStockValueCents() || 0,
        highest_stock: (stats.getHighestStockList() || []).map(stockExtremeFromProto).filter(Boolean),
        lowest_stock: (stats.getLowestStockList() || []).map(stockExtremeFromProto).filter(Boolean),
    };
}

export function replacementPartsListResponseFromProto(res) {
    return {
        success: true,
        data: {
            items: (res?.getItemsList() || []).map(replacementPartItemFromProto).filter(Boolean),
            ...paginationFromProto(res?.getMeta()),
            stats: replacementPartsStatsFromProto(res?.getStats()),
            archived_total: res?.getArchivedTotal?.() || 0,
        },
    };
}

export function operationalCostFromProto(item) {
    if (!item) {
        return null;
    }
    const uuid = item.getUuid() || item.getId() || '';
    const out = {
        id: uuid,
        uuid,
        name: item.getName() || '',
        category: item.getCategory() || '',
        amount_cents: item.getAmountCents() || 0,
        recurrence: item.getRecurrence() || 'recurring',
        notes: item.getNotes() || '',
        frequency: (typeof item.getFrequency === 'function' ? item.getFrequency() : '') || 'monthly',
        monthly_amount_cents: typeof item.getMonthlyAmountCents === 'function'
            ? (item.getMonthlyAmountCents() || 0)
            : 0,
    };
    return assignTimestampsFromProto(out, item, ['created_at', 'updated_at', 'archived_at']);
}

export function operationalCostsStatsFromProto(stats) {
    if (!stats) {
        return {
            total_items: 0,
            recurring_total_cents: 0,
            one_time_total_cents: 0,
            month_total_cents: 0,
        };
    }
    return {
        total_items: stats.getTotalItems() || 0,
        recurring_total_cents: stats.getRecurringTotalCents() || 0,
        one_time_total_cents: stats.getOneTimeTotalCents() || 0,
        month_total_cents: typeof stats.getMonthTotalCents === 'function'
            ? (stats.getMonthTotalCents() || 0)
            : 0,
    };
}

export function operationalCostsListResponseFromProto(res) {
    return {
        success: true,
        data: {
            items: (res?.getItemsList() || []).map(operationalCostFromProto).filter(Boolean),
            ...paginationFromProto(res?.getMeta()),
            stats: operationalCostsStatsFromProto(res?.getStats()),
            archived_total: typeof res?.getArchivedTotal === 'function' ? (res.getArchivedTotal() || 0) : 0,
        },
    };
}

export function printerModelFromProto(item) {
    if (!item) {
        return null;
    }
    const uuid = item.getUuid() || item.getId() || '';
    const out = {
        id: uuid,
        uuid,
        brand: item.getBrand() || '',
        model: item.getModel() || '',
        avg_consumption_watts: item.getAvgConsumptionWatts() || 0,
        max_consumption_watts: item.getMaxConsumptionWatts() || 0,
        farm_link_supported: Boolean(item.getFarmLinkSupported && item.getFarmLinkSupported()),
        experimental: Boolean(item.getExperimental && item.getExperimental()),
        maintenance_tasks: (item.getMaintenanceTasksList?.() || []).map(maintenanceTaskFromProto).filter(Boolean),
    };
    return assignTimestampsFromProto(out, item, ['created_at', 'updated_at']);
}

export function maintenanceTaskFromProto(task) {
    if (!task) {
        return null;
    }
    return {
        task_key: task.getTaskKey() || '',
        interval_hours: task.getIntervalHours() || 0,
        interval_days: task.getIntervalDays() || 0,
    };
}

export function maintenanceTaskTypeFromProto(task) {
    if (!task) {
        return null;
    }
    return {
        key: task.getKey() || '',
        label: task.getLabel() || '',
        description: task.getDescription() || '',
        default_interval_hours: task.getDefaultIntervalHours() || 0,
        default_interval_days: task.getDefaultIntervalDays() || 0,
        sort_order: task.getSortOrder() || 0,
    };
}

export function upcomingMaintenanceTaskFromProto(task) {
    if (!task) {
        return null;
    }
    const out = {
        task_key: task.getTaskKey() || '',
        label: task.getLabel() || '',
        description: task.getDescription() || '',
        interval_hours: task.getIntervalHours() || 0,
        interval_days: task.getIntervalDays() || 0,
        never_performed: Boolean(task.getNeverPerformed()),
        days_since: task.getDaysSince() || 0,
        wear_hours_since: task.getWearHoursSince() || 0,
        days_remaining: task.getDaysRemaining() || 0,
        hours_remaining: task.getHoursRemaining() || 0,
        status: task.getStatus() || 'ok',
        due_by: task.getDueBy() || '',
        suggested_hours_count: task.getSuggestedHoursCount() || 0,
    };
    return assignTimestampsFromProto(out, task, [
        'last_occurred_at',
        'reference_date',
        'next_due_date',
        'snoozed_until',
    ]);
}

export function upcomingMaintenancePrinterFromProto(printer) {
    if (!printer) {
        return null;
    }
    return {
        printer_uuid: printer.getPrinterUuid() || '',
        printer_name: printer.getPrinterName() || '',
        brand: printer.getBrand() || '',
        model: printer.getModel() || '',
        overdue_count: printer.getOverdueCount() || 0,
        due_soon_count: printer.getDueSoonCount() || 0,
        snoozed_count: printer.getSnoozedCount() || 0,
        tasks: (printer.getTasksList() || []).map(upcomingMaintenanceTaskFromProto).filter(Boolean),
    };
}

export function upcomingMaintenancesResponseFromProto(res) {
    return {
        success: Boolean(res?.getSuccess?.() ?? true),
        data: {
            printers: (res?.getPrintersList() || []).map(upcomingMaintenancePrinterFromProto).filter(Boolean),
            overdue_total: res?.getOverdueTotal?.() || 0,
            due_soon_total: res?.getDueSoonTotal?.() || 0,
            snoozed_total: res?.getSnoozedTotal?.() || 0,
        },
    };
}

export function printerModelsStatsFromProto(stats) {
    if (!stats) {
        return {
            total_models: 0,
            total_brands: 0,
        };
    }
    return {
        total_models: stats.getTotalModels() || 0,
        total_brands: stats.getTotalBrands() || 0,
    };
}

export function printerModelsListResponseFromProto(res) {
    return {
        success: true,
        data: {
            items: (res?.getItemsList() || []).map(printerModelFromProto).filter(Boolean),
            ...paginationFromProto(res?.getMeta()),
            stats: printerModelsStatsFromProto(res?.getStats()),
            brands: res?.getBrandsList ? res.getBrandsList() : [],
        },
    };
}

export function salesChannelFromProto(m) {
    if (!m) {
        return null;
    }
    const out = {
        uuid: m.getUuid() || '',
        id: m.getUuid() || '',
        name: m.getName() || '',
        commission_percent: m.getCommissionPercent?.() || 0,
        fixed_fee: m.getFixedFee?.() || 0,
        payment_fee_percent: m.getPaymentFeePercent?.() || 0,
        min_fee: m.getMinFee?.() || 0,
        notes: m.getNotes?.() || '',
        requires_external_order_id: typeof m.getRequiresExternalOrderId === 'function'
            ? Boolean(m.getRequiresExternalOrderId())
            : true,
        kind: typeof m.getKind === 'function' ? (m.getKind() || 'marketplace') : 'marketplace',
        holds_payout: typeof m.getHoldsPayout === 'function' ? Boolean(m.getHoldsPayout()) : false,
        dispatch_days: null,
    };
    if (typeof m.hasDispatchDays === 'function' && m.hasDispatchDays()) {
        out.dispatch_days = m.getDispatchDays();
    }
    return assignTimestampsFromProto(out, m, ['created_at', 'updated_at']);
}

const PAYMENT_FEE_METHOD_LABELS = {
    credit: 'Crédito',
    debit: 'Débito',
    pix: 'PIX',
};

export function paymentFeeFromProto(m) {
    if (!m) {
        return null;
    }
    const method = m.getPaymentMethod() || '';
    const validTo = m.getValidTo() || '';
    const out = {
        uuid: m.getUuid() || '',
        id: m.getUuid() || '',
        payment_method: method,
        payment_method_label: PAYMENT_FEE_METHOD_LABELS[method] || method,
        fee_percent: m.getFeePercent?.() || 0,
        valid_from: m.getValidFrom() || '',
        valid_to: validTo || null,
        notes: m.getNotes?.() || '',
    };
    return assignTimestampsFromProto(out, m, ['created_at', 'updated_at']);
}

export function appNotificationFromProto(n) {
    if (!n) {
        return null;
    }
    const out = {
        id: n.getId() || '',
        title: n.getTitle() || '',
        body: n.getBody() || '',
        link: n.getLink() || '',
        broadcast: Boolean(n.getBroadcast()),
        read: Boolean(n.getRead()),
        target_plan_names: typeof n.getTargetPlanNamesList === 'function'
            ? (n.getTargetPlanNamesList() || [])
            : [],
        audience: typeof n.getAudience === 'function' ? (n.getAudience() || '') : '',
        target_user_name: typeof n.getTargetUserName === 'function' ? (n.getTargetUserName() || '') : '',
        target_user_email: typeof n.getTargetUserEmail === 'function' ? (n.getTargetUserEmail() || '') : '',
    };
    if (n.getTargetUserUuid()) {
        out.target_user_uuid = n.getTargetUserUuid();
    }
    const createdAt = timestampToISO(n.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    return out;
}

export function unreadCountResponseFromProto(res) {
    return {
        success: true,
        data: {
            unread_count: res?.getUnreadCount?.() || 0,
        },
    };
}

export function createAppNotificationResponseFromProto(res) {
    return {
        success: true,
        notification: appNotificationFromProto(res?.getNotification?.()),
    };
}

export function desktopReleaseFromProto(r) {
    if (!r) {
        return null;
    }
    const id = r.getId() || '';
    const out = {
        id,
        uuid: id,
        version: r.getVersion() || '',
        title: r.getTitle() || '',
        changelog: r.getChangelog() || '',
        download_windows: r.getDownloadWindows() || '',
        download_mac: r.getDownloadMac() || '',
        download_linux: r.getDownloadLinux() || '',
        active: typeof r.getActive === 'function' ? r.getActive() !== false : true,
    };
    const publishedAt = timestampToISO(r.getPublishedAt());
    if (publishedAt) {
        out.published_at = publishedAt;
    }
    return assignTimestampsFromProto(out, r, ['created_at', 'updated_at']);
}

export function tutorialVideoFromProto(item) {
    if (!item) {
        return null;
    }
    const uuid = item.getUuid() || item.getId() || '';
    const out = {
        id: uuid,
        uuid,
        name: item.getName() || '',
        desktop_url: item.getDesktopUrl() || '',
        mobile_url: item.getMobileUrl() || '',
        section: item.getSection() || '',
        page_keys: typeof item.getPageKeysList === 'function' ? (item.getPageKeysList() || []) : [],
        sort_order: typeof item.getSortOrder === 'function' ? (item.getSortOrder() || 0) : 0,
    };
    return assignTimestampsFromProto(out, item, ['created_at', 'updated_at']);
}

export function ticketOptionFromProto(o) {
    if (!o) {
        return null;
    }
    return {
        code: o.getCode() || '',
        label: o.getLabel() || '',
    };
}

export function ticketAttachmentFromProto(a) {
    if (!a) {
        return null;
    }
    const id = a.getId() || '';
    const out = {
        id,
        uuid: id,
        ticket_uuid: a.getTicketUuid() || '',
        message_uuid: a.getMessageUuid() || '',
        file_name: a.getFileName() || '',
        content_type: a.getContentType() || '',
        size_bytes: a.getSizeBytes?.() || 0,
        url: a.getUrl() || '',
    };
    return assignTimestampsFromProto(out, a, ['created_at']);
}

export function ticketMessageFromProto(m) {
    if (!m) {
        return null;
    }
    const id = m.getId() || '';
    const out = {
        id,
        uuid: id,
        ticket_uuid: m.getTicketUuid() || '',
        author_user_uuid: m.getAuthorUserUuid() || '',
        author_name: m.getAuthorName() || '',
        body: m.getBody() || '',
        is_staff: Boolean(m.getIsStaff()),
        attachments: (m.getAttachmentsList?.() || []).map(ticketAttachmentFromProto).filter(Boolean),
    };
    return assignTimestampsFromProto(out, m, ['created_at']);
}

export function ticketFromProto(t) {
    if (!t) {
        return null;
    }
    const id = t.getId() || '';
    const out = {
        id,
        uuid: id,
        user_uuid: t.getUserUuid() || '',
        user_name: t.getUserName() || '',
        user_email: t.getUserEmail() || '',
        category: t.getCategory() || '',
        category_label: t.getCategoryLabel() || '',
        channel: typeof t.getChannel === 'function' ? (t.getChannel() || '') : '',
        channel_label: typeof t.getChannelLabel === 'function' ? (t.getChannelLabel() || '') : '',
        status: t.getStatus() || '',
        status_label: t.getStatusLabel() || '',
        subject: t.getSubject() || '',
        messages: (t.getMessagesList?.() || []).map(ticketMessageFromProto).filter(Boolean),
        attachments: (t.getAttachmentsList?.() || []).map(ticketAttachmentFromProto).filter(Boolean),
    };
    return assignTimestampsFromProto(out, t, ['created_at', 'updated_at']);
}

export function ticketMetaFromProto(res) {
    return {
        success: true,
        categories: (res?.getCategoriesList?.() || []).map(ticketOptionFromProto).filter(Boolean),
        statuses: (res?.getStatusesList?.() || []).map(ticketOptionFromProto).filter(Boolean),
        channels: (res?.getChannelsList?.() || []).map(ticketOptionFromProto).filter(Boolean),
    };
}

export function productListItemFromProto(p) {
    if (!p) {
        return null;
    }
    const id = p.getId() || '';
    const out = {
        id,
        uuid: id,
        name: p.getName() || '',
        base_price: p.getBasePrice() || 0,
        stock_quantity: p.getStockQuantity() || 0,
        total_sales_quantity: p.getTotalSalesQuantity() || 0,
        kind: typeof p.getKind === 'function' ? (p.getKind() || 'physical_stock') : 'physical_stock',
    };
    const createdAt = timestampToISO(p.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    return out;
}

export function productFromProto(p) {
    if (!p) {
        return null;
    }
    const availability = p.getAvailability();
    const measures = parseJsonField(p.getMeasures());
    const id = p.getId() || p.getUuid() || '';
    const out = {
        id,
        uuid: p.getUuid() || id,
        user_uuid: p.getUserUuid() || '',
        name: p.getName() || '',
        description: p.getDescription() || '',
        base_price: p.getBasePrice() || 0,
        cost_price: p.getCostPrice() || 0,
        stock_quantity: p.getStockQuantity() || 0,
        total_sales_quantity: p.getTotalSalesQuantity() || 0,
        is_customizable: Boolean(p.getIsCustomizable()),
        assembly_required: Boolean(p.getAssemblyRequired()),
        image: p.getImage() || '',
        images: p.getImagesList() || [],
        kind: typeof p.getKind === 'function' ? (p.getKind() || 'physical_stock') : 'physical_stock',
    };
    if (measures != null) {
        out.measures = measures;
        out.measurements = measures;
    }
    if (availability) {
        out.availability = {
            lead_time_for_order: availability.getLeadTimeForOrder() || 0,
        };
    }
    return assignTimestampsFromProto(out, p, ['created_at', 'updated_at']);
}

export function customerListItemFromProto(c) {
    if (!c) {
        return null;
    }
    const id = c.getId() || '';
    const out = {
        id,
        uuid: id,
        name: c.getName() || '',
        phone: c.getPhone() || '',
        email: c.getEmail() || '',
        document: c.getDocument() || '',
        category: c.getCategory?.() || 'cliente',
        address: c.getAddress?.() || '',
        address_number: c.getAddressNumber?.() || '',
        address_complement: c.getAddressComplement?.() || '',
        neighborhood: c.getNeighborhood?.() || '',
        city: c.getCity?.() || '',
        state: c.getState?.() || '',
        zip_code: c.getZipCode?.() || '',
        orders_count: c.getOrdersCount?.() || 0,
        orders_total_cents: c.getOrdersTotalCents?.() || 0,
        average_ticket_cents: c.getAverageTicketCents?.() || 0,
        unpaid_orders_count: c.getUnpaidOrdersCount?.() || 0,
        unpaid_amount_cents: c.getUnpaidAmountCents?.() || 0,
        origin_sales_channel_uuid: typeof c.getOriginSalesChannelUuid === 'function'
            ? (c.getOriginSalesChannelUuid() || '')
            : '',
        origin_sales_channel_name: typeof c.getOriginSalesChannelName === 'function'
            ? (c.getOriginSalesChannelName() || '')
            : '',
    };
    const createdAt = timestampToISO(c.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    const lastOrderAt = timestampToISO(c.getLastOrderAt?.());
    if (lastOrderAt) {
        out.last_order_at = lastOrderAt;
    }
    return out;
}

export function customerFromProto(c) {
    if (!c) {
        return null;
    }
    const doc = c.getDocument() || c.getDocumentNumber() || '';
    const out = {
        uuid: c.getUuid() || '',
        name: c.getName() || '',
        phone: c.getPhone() || '',
        email: c.getEmail() || '',
        document: doc,
        document_number: doc,
        category: c.getCategory?.() || 'cliente',
        address: c.getStreet() || '',
        address_number: c.getNumber() || '',
        address_complement: c.getComplement() || '',
        neighborhood: c.getNeighborhood() || '',
        city: c.getCity() || '',
        state: c.getState() || '',
        zip_code: c.getZipCode() || '',
        orders_count: c.getOrdersCount?.() || 0,
        orders_total_cents: c.getOrdersTotalCents?.() || 0,
        average_ticket_cents: c.getAverageTicketCents?.() || 0,
        unpaid_orders_count: c.getUnpaidOrdersCount?.() || 0,
        unpaid_amount_cents: c.getUnpaidAmountCents?.() || 0,
        origin_sales_channel_uuid: typeof c.getOriginSalesChannelUuid === 'function'
            ? (c.getOriginSalesChannelUuid() || '')
            : '',
        origin_sales_channel_name: typeof c.getOriginSalesChannelName === 'function'
            ? (c.getOriginSalesChannelName() || '')
            : '',
    };
    const lastOrderAt = timestampToISO(c.getLastOrderAt?.());
    if (lastOrderAt) {
        out.last_order_at = lastOrderAt;
    }
    return assignTimestampsFromProto(out, c, ['created_at', 'updated_at']);
}

function checkoutLikeFromProto(c, extra = {}) {
    if (!c) {
        return null;
    }
    const paymentMethods = typeof c.getPaymentMethodsList === 'function'
        ? c.getPaymentMethodsList()
        : [];
    const out = {
        id: c.getId() || '',
        uuid: c.getId() || '',
        customer: customerRefFromProto(c.getCustomer()),
        shipping_method: shippingMethodRefFromProto(c.getShippingMethod()),
        products: (c.getProductsList() || []).map(checkoutProductFromProto).filter(Boolean),
        amount: c.getAmount() || 0,
        sales_channel: salesChannelRefFromProto(c.getSalesChannel()),
        payment_methods: (paymentMethods || []).map(paymentMethodFromProto).filter(Boolean),
        payment_details: paymentDetailsFromProto(c.getPaymentDetails()),
        photo: c.getPhoto() || '',
        photo_url: c.getPhotoUrl() || '',
        ...extra,
    };
    if (typeof c.getExpectedNet === 'function') {
        out.expected_net = c.getExpectedNet() || 0;
    }
    if (typeof c.getBankReconciled === 'function') {
        out.bank_reconciled = Boolean(c.getBankReconciled());
    }
    if (typeof c.getFinishOrder === 'function') {
        out.finish_order = Boolean(c.getFinishOrder());
    }
    if (typeof c.getPaymentReceived === 'function') {
        out.payment_received = Boolean(c.getPaymentReceived());
    }
    if (typeof c.getIsQuickCheckout === 'function') {
        out.is_quick_checkout = Boolean(c.getIsQuickCheckout());
    }
    if (typeof c.getUseQuickCheckoutCreationDate === 'function') {
        out.use_quick_checkout_creation_date = Boolean(c.getUseQuickCheckoutCreationDate());
    }
    if (typeof c.getObservations === 'function') {
        out.observations = c.getObservations() || '';
    }
    if (typeof c.getCommission === 'function') {
        out.commission = c.getCommission() || 0;
    }
    if (typeof c.getDiscount === 'function') {
        out.discount = c.getDiscount() || 0;
    }
    if (typeof c.getDiscountReason === 'function') {
        out.discount_reason = c.getDiscountReason() || '';
    }
    if (typeof c.getExternalOrderId === 'function') {
        out.external_order_id = c.getExternalOrderId() || '';
    }
    if (typeof c.getExternalOrderUrl === 'function') {
        out.external_order_url = c.getExternalOrderUrl() || '';
    }
    if (typeof c.getProductionStatus === 'function') {
        out.production_status = c.getProductionStatus() || '';
    }
    if (typeof c.getLogisticsStatus === 'function') {
        out.logistics_status = c.getLogisticsStatus() || '';
    }
    if (typeof c.getPayoutStatus === 'function') {
        out.payout_status = c.getPayoutStatus() || '';
    }
    if (typeof c.getChannelKind === 'function') {
        out.channel_kind = c.getChannelKind() || '';
    }
    if (typeof c.getCompleted === 'function') {
        out.completed = Boolean(c.getCompleted());
    }
    return assignTimestampsFromProto(out, c, ['completed_at', 'created_at', 'updated_at']);
}

export function checkoutListItemFromProto(c) {
    if (!c) {
        return null;
    }
    return {
        id: c.getId() || '',
        customer: customerRefFromProto(c.getCustomer()),
        products: (c.getProductsList() || []).map(checkoutProductFromProto).filter(Boolean),
        sales_channel: salesChannelRefFromProto(c.getSalesChannel()),
        amount: c.getAmount() || 0,
        photo: c.getPhoto() || '',
        photo_url: c.getPhotoUrl() || '',
        created_at: timestampToISO(c.getCreatedAt()) || undefined,
    };
}

export function checkoutFromProto(c) {
    return checkoutLikeFromProto(c);
}

export function quoteShippingOptionFromProto(opt) {
    if (!opt) {
        return null;
    }
    return {
        name: opt.getName() || '',
        cost: opt.getCost() || 0,
        delivery_days: opt.getDeliveryDays() || 0,
    };
}

export function quotePaymentOptionFromProto(opt) {
    if (!opt) {
        return null;
    }
    return {
        name: opt.getName() || '',
        installments: opt.getInstallments() || 1,
        amount: opt.getAmount() || 0,
    };
}

export function quoteFromProto(q) {
    if (!q) {
        return null;
    }
    const uuid = q.getUuid() || '';
    const out = {
        uuid,
        id: uuid,
        customer_uuid: q.getCustomerUuid() || '',
        customer_name: q.getCustomerName() || '',
        customer_phone: q.getCustomerPhone() || '',
        customer_email: q.getCustomerEmail() || '',
        customer_category: q.getCustomerCategory() || '',
        status: q.getStatus() || '',
        products: (q.getProductsList() || []).map(checkoutProductFromProto).filter(Boolean),
        amount: q.getAmount() || 0,
        discount: q.getDiscount() || 0,
        discount_reason: typeof q.getDiscountReason === 'function' ? (q.getDiscountReason() || '') : '',
        production_days: q.getProductionDays() || 0,
        valid_until: q.getValidUntil() || '',
        shipping_options: (q.getShippingOptionsList() || []).map(quoteShippingOptionFromProto).filter(Boolean),
        payment_options: (q.getPaymentOptionsList() || []).map(quotePaymentOptionFromProto).filter(Boolean),
        observations: q.getObservations() || '',
        checkout_uuid: q.getCheckoutUuid() || '',
        order_uuid: q.getCheckoutUuid() || '',
    };
    const quotaCountedAt = timestampToISO(q.getQuotaCountedAt());
    if (quotaCountedAt) {
        out.quota_counted_at = quotaCountedAt;
    }
    const archivedAt = timestampToISO(q.getArchivedAt());
    if (archivedAt) {
        out.archived_at = archivedAt;
    }
    const createdAt = timestampToISO(q.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    const updatedAt = timestampToISO(q.getUpdatedAt());
    if (updatedAt) {
        out.updated_at = updatedAt;
    }
    return out;
}

export function quoteListItemFromProto(q) {
    if (!q) {
        return null;
    }
    const id = q.getId() || '';
    const out = {
        id,
        uuid: id,
        customer_name: q.getCustomerName() || '',
        customer_phone: q.getCustomerPhone() || '',
        customer_category: q.getCustomerCategory() || '',
        status: q.getStatus() || '',
        amount: q.getAmount() || 0,
        valid_until: q.getValidUntil() || '',
        production_days: q.getProductionDays() || 0,
        checkout_uuid: q.getCheckoutUuid() || '',
        order_uuid: q.getCheckoutUuid() || '',
    };
    const archivedAt = timestampToISO(q.getArchivedAt());
    if (archivedAt) {
        out.archived_at = archivedAt;
    }
    const createdAt = timestampToISO(q.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    return out;
}

export function orderTopProductFromProto(p) {
    if (!p) {
        return null;
    }
    return {
        name: p.getName() || '',
        quantity: p.getQuantity() || 0,
    };
}

export function productSummaryFromProto(res) {
    if (!res) {
        return {
            total_products: 0,
            min_price: 0,
            max_price: 0,
            average_price: 0,
            total_sales_quantity: 0,
            total_stock_quantity: 0,
            top_products: [],
        };
    }
    return {
        total_products: res.getTotalProducts() || 0,
        min_price: res.getMinPrice() || 0,
        max_price: res.getMaxPrice() || 0,
        average_price: res.getAveragePrice() || 0,
        total_sales_quantity: res.getTotalSalesQuantity() || 0,
        total_stock_quantity: res.getTotalStockQuantity() || 0,
        top_products: (res.getTopProductsList() || []).map(orderTopProductFromProto).filter(Boolean),
    };
}

export function orderSummaryFromProto(res) {
    if (!res) {
        return {
            total_sales: 0,
            total_products: 0,
            total_amount: 0,
            average_ticket: 0,
            top_products: [],
            from: '',
            to: '',
        };
    }
    return {
        total_sales: res.getTotalSales() || 0,
        total_products: res.getTotalProducts() || 0,
        total_amount: res.getTotalAmount() || 0,
        average_ticket: res.getAverageTicket() || 0,
        top_products: (res.getTopProductsList() || []).map(orderTopProductFromProto).filter(Boolean),
        from: res.getFrom() || '',
        to: res.getTo() || '',
    };
}

export function orderListItemFromProto(o) {
    if (!o) {
        return null;
    }
    const out = {
        id: o.getId() || '',
        customer: customerRefFromProto(o.getCustomer()),
        products: (o.getProductsList() || []).map(checkoutProductFromProto).filter(Boolean),
        sales_channel: salesChannelRefFromProto(o.getSalesChannel()),
        shipping_method: shippingMethodRefFromProto(o.getShippingMethod()),
        amount: o.getAmount() || 0,
        expected_net: typeof o.getExpectedNet === 'function' ? (o.getExpectedNet() || 0) : (o.getAmount() || 0),
        payment_methods: (o.getPaymentMethodsList() || []).map(paymentMethodFromProto).filter(Boolean),
        payment_details: paymentDetailsFromProto(o.getPaymentDetails()),
        payment_received: Boolean(o.getPaymentReceived()),
        bank_reconciled: typeof o.getBankReconciled === 'function' ? Boolean(o.getBankReconciled()) : false,
        external_order_id: typeof o.getExternalOrderId === 'function' ? (o.getExternalOrderId() || '') : '',
        external_order_url: typeof o.getExternalOrderUrl === 'function' ? (o.getExternalOrderUrl() || '') : '',
        production_status: typeof o.getProductionStatus === 'function' ? (o.getProductionStatus() || '') : '',
        logistics_status: typeof o.getLogisticsStatus === 'function' ? (o.getLogisticsStatus() || '') : '',
        payout_status: typeof o.getPayoutStatus === 'function' ? (o.getPayoutStatus() || '') : '',
        channel_kind: typeof o.getChannelKind === 'function' ? (o.getChannelKind() || '') : '',
        completed: typeof o.getCompleted === 'function' ? Boolean(o.getCompleted()) : false,
    };
    const createdAt = timestampToISO(o.getCreatedAt());
    if (createdAt) {
        out.created_at = createdAt;
    }
    if (typeof o.getCompletedAt === 'function') {
        const completedAt = timestampToISO(o.getCompletedAt());
        if (completedAt) {
            out.completed_at = completedAt;
            out.completed = true;
        }
    }
    return out;
}

export function orderFromProto(o) {
    return checkoutLikeFromProto(o);
}

export function transactionFromProto(t) {
    if (!t) {
        return null;
    }
    const out = {
        id: t.getId() || '',
        type: t.getType() || '',
        amount: t.getAmount() || 0,
        deduction: t.getDeduction() || 0,
        net_amount: t.getNetAmount() || 0,
        description: t.getDescription() || '',
        payment_method: t.getPaymentMethod() || '',
        order_id: t.getOrderId() || '',
        authorization_id: typeof t.getAuthorizationId === 'function' ? (t.getAuthorizationId() || '') : '',
        source: typeof t.getSource === 'function' ? (t.getSource() || '') : '',
        outcome_category: typeof t.getOutcomeCategory === 'function' ? (t.getOutcomeCategory() || '') : '',
        bank_reconciled: typeof t.getAuthorizationId === 'function'
            ? Boolean(String(t.getAuthorizationId() || '').trim())
            : false,
    };
    return assignTimestampsFromProto(out, t, ['created_at', 'updated_at']);
}

export function transactionSummaryFromProto(t) {
    return transactionFromProto(t);
}

export function reconciliationFromProto(data) {
    if (!data) {
        return { success: true, data: {} };
    }
    const dailyList = typeof data.getDailyList === 'function' ? (data.getDailyList() || []) : [];
    return {
        success: true,
        data: {
            total_income: data.getTotalIncome() || 0,
            total_outcome: data.getTotalOutcome() || 0,
            balance: data.getBalance() || 0,
            transaction_count: data.getTransactionCount() || 0,
            income_count: data.getIncomeCount() || 0,
            outcome_count: data.getOutcomeCount() || 0,
            average_transaction: data.getAverageTransaction() || 0,
            expense_total: typeof data.getExpenseTotal === 'function' ? (data.getExpenseTotal() || 0) : 0,
            withdrawal_total: typeof data.getWithdrawalTotal === 'function' ? (data.getWithdrawalTotal() || 0) : 0,
            expense_count: typeof data.getExpenseCount === 'function' ? (data.getExpenseCount() || 0) : 0,
            withdrawal_count: typeof data.getWithdrawalCount === 'function' ? (data.getWithdrawalCount() || 0) : 0,
            daily: dailyList.map((point) => ({
                date: point.getDate?.() || '',
                income_total: point.getIncomeTotal?.() || 0,
                outcome_total: point.getOutcomeTotal?.() || 0,
                withdrawal_total: point.getWithdrawalTotal?.() || 0,
            })),
        },
    };
}

export function jobFilamentFromProto(f) {
    if (!f) {
        return null;
    }
    return {
        color: f.getColor() || '',
        filament_uuid: f.getFilamentUuid() || '',
        grams: f.getGrams() || 0,
        color_label: f.getColorLabel() || '',
        type: f.getType() || '',
        brand: f.getBrand() || '',
    };
}

export function jobFromProto(j) {
    if (!j) {
        return null;
    }
    const metadata = parseJsonField(j.getMetadataJson());
    const out = {
        id: j.getId() || j.getUuid() || '',
        uuid: j.getUuid() || j.getId() || '',
        printer_uuid: j.getPrinterUuid() || '',
        job_id: j.getJobId() || '',
        file_name: j.getFileName() || '',
        estimated_duration_minutes: j.getEstimatedDurationMinutes() || 0,
        estimated_filament_consumption_grams: j.getEstimatedFilamentConsumptionGrams() || 0,
        status: j.getStatus() || '',
        status_reason: j.getStatusReason() || '',
        object_count: j.getObjectCount() || 0,
        printed_count: j.getPrintedCount() || 0,
        success_percent: j.getSuccessPercent() || 0,
        filaments: (j.getFilamentsList() || []).map(jobFilamentFromProto).filter(Boolean),
        filament_pending: Boolean(j.getFilamentPending?.()),
    };
    // Absent means the real consumption was never informed, which is different
    // from zero.
    if (j.hasActualFilamentConsumptionGrams?.()) {
        out.actual_filament_consumption_grams = j.getActualFilamentConsumptionGrams() || 0;
    }
    if (metadata != null) {
        out.metadata = metadata;
    }
    return assignTimestampsFromProto(out, j, ['started_at', 'finished_at', 'created_at', 'updated_at']);
}

export function printerStatFromProto(s) {
    if (!s) {
        return null;
    }
    return {
        printer_uuid: s.getPrinterUuid() || '',
        total_jobs: s.getTotalJobs() || 0,
        total_objects: s.getTotalObjects() || 0,
        total_printed: s.getTotalPrinted() || 0,
        success_rate: s.getSuccessRate() || 0,
        total_duration_minutes: s.getTotalDurationMinutes() || 0,
        total_filament_grams: s.getTotalFilamentGrams() || 0,
        filament_pending_jobs: s.getFilamentPendingJobs?.() || 0,
        filaments: typeof s.getFilamentsList === 'function'
            ? (s.getFilamentsList() || []).map(printerFilamentAssignmentFromProto).filter(Boolean)
            : [],
    };
}

export function printerStatsResponseFromProto(res) {
    return {
        success: true,
        data: {
            printers: (res?.getPrintersList() || []).map(printerStatFromProto).filter(Boolean),
        },
    };
}

export function replacementPartFromProto(p) {
    if (!p) {
        return null;
    }
    const stockUuid = typeof p.getStockPartUuid === 'function' ? p.getStockPartUuid() : '';
    return {
        name: p.getName() || '',
        value_cents: p.getValueCents() || 0,
        stock_part_uuid: stockUuid || '',
    };
}

export function printerMaintenanceFromProto(m) {
    if (!m) {
        return null;
    }
    const out = {
        id: m.getId() || m.getUuid() || '',
        uuid: m.getUuid() || m.getId() || '',
        printer_uuid: m.getPrinterUuid() || '',
        type: m.getType() || '',
        problem_description: m.getProblemDescription() || '',
        analysis_duration_minutes: m.getAnalysisDurationMinutes() || 0,
        performed_actions: m.getPerformedActions() || '',
        printer_hours_count: m.getPrinterHoursCount() || 0,
        replacement_parts: (m.getReplacementPartsList() || []).map(replacementPartFromProto).filter(Boolean),
        correction_duration_minutes: m.getCorrectionDurationMinutes() || 0,
    };
    return assignTimestampsFromProto(out, m, ['occurred_at', 'created_at', 'updated_at']);
}

export function successResponseFromProto(res, fallbackMessage = '') {
    return {
        success: Boolean(res?.getSuccess?.() ?? true),
        message: res?.getMessage?.() || fallbackMessage,
    };
}

export function deleteResponseFromProto(res) {
    return {
        success: Boolean(res?.getSuccess?.() ?? true),
        message: res?.getMessage?.() || '',
    };
}

export function featureFlagAllowlistFromProto(row) {
    if (!row) {
        return null;
    }
    return {
        feature_key: row.getFeatureKey?.() || '',
        user_uuid: row.getUserUuid?.() || '',
        user_name: row.getUserName?.() || '',
        user_email: row.getUserEmail?.() || '',
        created_at: timestampToISO(row.getCreatedAt?.()),
    };
}

export function featureFlagFromProto(flag) {
    if (!flag) {
        return null;
    }
    return {
        key: flag.getKey() || '',
        name: flag.getName() || '',
        description: flag.getDescription() || '',
        enabled: Boolean(flag.getEnabled()),
        is_beta: Boolean(flag.getIsBeta()),
        show_in_menu: Boolean(flag.getShowInMenu?.()),
        plan_names: flag.getPlanNamesList?.() || [],
        allowlist: (flag.getAllowlistList?.() || []).map(featureFlagAllowlistFromProto).filter(Boolean),
        created_at: timestampToISO(flag.getCreatedAt?.()),
        updated_at: timestampToISO(flag.getUpdatedAt?.()),
    };
}

export function billingExemptionFromProto(item) {
    if (!item) {
        return null;
    }
    return {
        uuid: item.getUuid?.() || '',
        user_uuid: item.getUserUuid?.() || '',
        user_name: item.getUserName?.() || '',
        user_email: item.getUserEmail?.() || '',
        reason: item.getReason?.() || '',
        exempt_until: item.getIndefinite?.() ? null : timestampToISO(item.getExemptUntil?.()),
        indefinite: Boolean(item.getIndefinite?.()),
        created_at: timestampToISO(item.getCreatedAt?.()),
        updated_at: timestampToISO(item.getUpdatedAt?.()),
        created_by: item.getCreatedBy?.() || '',
    };
}

/** Limites de cota do plano: chave -> número, onde -1 significa ilimitado. */
function planLimitsFromProto(plan) {
    const out = {};
    (plan?.getLimitsList?.() || []).forEach((limit) => {
        const key = limit?.getKey?.() || '';
        if (key) {
            out[key] = Number(limit.getValue?.() || 0);
        }
    });
    return out;
}

export function subscriptionPlanFromProto(plan) {
    if (!plan) {
        return null;
    }
    return {
        uuid: plan.getUuid?.() || '',
        name: plan.getName?.() || '',
        description: plan.getDescription?.() || '',
        monthly_billed_value: Number(plan.getMonthlyBilledValue?.() || 0),
        features: plan.getFeaturesList?.() || [],
        limits: planLimitsFromProto(plan),
    };
}

export function evaluatedFeaturesFromProto(res) {
    const map = res?.getDataMap?.();
    const out = {};
    if (map && typeof map.forEach === 'function') {
        map.forEach((value, key) => {
            if (value && typeof value.getAccess === 'function') {
                out[key] = {
                    access: Boolean(value.getAccess()),
                    show_in_menu: Boolean(value.getShowInMenu?.()),
                };
                return;
            }
            out[key] = {
                access: Boolean(value),
                show_in_menu: Boolean(value),
            };
        });
    }
    return { data: out };
}

function commercePartnerItemFromProto(item) {
    if (!item) {
        return null;
    }
    return {
        uuid: item.getUuid?.() || '',
        partner_uuid: item.getPartnerUuid?.() || '',
        product_uuid: item.getProductUuid?.() || '',
        product_name: item.getProductName?.() || '',
        item_kind: item.getItemKind?.() || '',
        min_initial_qty: item.getMinInitialQty?.() || 0,
        min_reorder_qty: item.getMinReorderQty?.() || 0,
        display_qty: item.getDisplayQty?.() || 0,
        wholesale_cents: item.getWholesaleCents?.() || 0,
        suggested_retail_cents: item.getSuggestedRetailCents?.() || 0,
        consignment_repasse_cents: item.getConsignmentRepasseCents?.() || 0,
    };
}

function commercePartnerStockFromProto(row) {
    if (!row) {
        return null;
    }
    return {
        partner_uuid: row.getPartnerUuid?.() || '',
        product_uuid: row.getProductUuid?.() || '',
        product_name: row.getProductName?.() || '',
        quantity_on_hand: row.getQuantityOnHand?.() || 0,
    };
}

export function commercePartnerFromProto(p) {
    if (!p) {
        return null;
    }
    const out = {
        uuid: p.getUuid() || '',
        user_uuid: p.getUserUuid?.() || '',
        customer_uuid: p.getCustomerUuid?.() || '',
        name: p.getName() || '',
        contact_name: p.getContactName?.() || '',
        contact_phone: p.getContactPhone?.() || '',
        contact_email: p.getContactEmail?.() || '',
        modality: p.getModality?.() || '',
        status: p.getStatus?.() || '',
        address: p.getAddress?.() || '',
        address_number: p.getAddressNumber?.() || '',
        address_complement: p.getAddressComplement?.() || '',
        neighborhood: p.getNeighborhood?.() || '',
        zip_code: p.getZipCode?.() || '',
        city: p.getCity?.() || '',
        state: p.getState?.() || '',
        visit_frequency: p.getVisitFrequency?.() || '',
        first_visit_on: p.getFirstVisitOn?.() || '',
        notes: p.getNotes?.() || '',
        items: (p.getItemsList?.() || []).map(commercePartnerItemFromProto).filter(Boolean),
        stock: (p.getStockList?.() || []).map(commercePartnerStockFromProto).filter(Boolean),
    };
    if (p.getHasCoordinates?.()) {
        out.latitude = p.getLatitude();
        out.longitude = p.getLongitude();
    }
    return assignTimestampsFromProto(out, p, ['created_at', 'updated_at']);
}

function commercePartnerVisitLineFromProto(line) {
    if (!line) {
        return null;
    }
    return {
        uuid: line.getUuid?.() || '',
        visit_uuid: line.getVisitUuid?.() || '',
        product_uuid: line.getProductUuid?.() || '',
        product_name: line.getProductName?.() || '',
        sold_qty: line.getSoldQty?.() || 0,
        restocked_qty: line.getRestockedQty?.() || 0,
        ordered_qty: line.getOrderedQty?.() || 0,
        unit_price_cents: line.getUnitPriceCents?.() || 0,
    };
}

export function commercePartnerVisitFromProto(v) {
    if (!v) {
        return null;
    }
    const out = {
        uuid: v.getUuid() || '',
        partner_uuid: v.getPartnerUuid?.() || '',
        user_uuid: v.getUserUuid?.() || '',
        planned_on: v.getPlannedOn?.() || '',
        visited_on: v.getVisitedOn?.() || '',
        status: v.getStatus?.() || '',
        notes: v.getNotes?.() || '',
        order_uuid: v.getOrderUuid?.() || '',
        partner_name: v.getPartnerName?.() || '',
        partner_modality: v.getPartnerModality?.() || '',
        partner_address: v.getPartnerAddress?.() || '',
        partner_city: v.getPartnerCity?.() || '',
        partner_contact_name: v.getPartnerContactName?.() || '',
        partner_contact_phone: v.getPartnerContactPhone?.() || '',
        receipt_json: v.getReceiptJson?.() || '',
        lines: (v.getLinesList?.() || []).map(commercePartnerVisitLineFromProto).filter(Boolean),
        partner_items: (v.getPartnerItemsList?.() || []).map(commercePartnerItemFromProto).filter(Boolean),
        partner_stock: (v.getPartnerStockList?.() || []).map(commercePartnerStockFromProto).filter(Boolean),
    };
    if (out.receipt_json) {
        try {
            out.receipt = JSON.parse(out.receipt_json);
        } catch {
            out.receipt = null;
        }
    }
    if (v.getPartnerHasCoordinates?.()) {
        out.partner_latitude = v.getPartnerLatitude();
        out.partner_longitude = v.getPartnerLongitude();
    }
    return assignTimestampsFromProto(out, v, ['completed_at', 'created_at', 'updated_at']);
}

export function commercePartnersDashboardFromProto(res) {
    return {
        data: {
            today_count: res?.getTodayCount?.() || 0,
            tomorrow_count: res?.getTomorrowCount?.() || 0,
            today_visits: (res?.getTodayVisitsList?.() || []).map(commercePartnerVisitFromProto).filter(Boolean),
            tomorrow_visits: (res?.getTomorrowVisitsList?.() || []).map(commercePartnerVisitFromProto).filter(Boolean),
        },
    };
}

export function commercePartnersMapFromProto(res) {
    const origin = res?.getOrigin?.();
    return {
        data: {
            date: res?.getDate?.() || '',
            origin: origin ? {
                label: origin.getLabel?.() || '',
                address: origin.getAddress?.() || '',
                latitude: origin.getHasCoordinates?.() ? origin.getLatitude() : null,
                longitude: origin.getHasCoordinates?.() ? origin.getLongitude() : null,
            } : {},
            pins: (res?.getPinsList?.() || []).map((pin) => ({
                visit_uuid: pin.getVisitUuid?.() || '',
                partner_uuid: pin.getPartnerUuid?.() || '',
                label: pin.getLabel?.() || '',
                status: pin.getStatus?.() || '',
                address: pin.getAddress?.() || '',
                city: pin.getCity?.() || '',
                latitude: pin.getHasCoordinates?.() ? pin.getLatitude() : null,
                longitude: pin.getHasCoordinates?.() ? pin.getLongitude() : null,
            })),
            visits: (res?.getVisitsList?.() || []).map(commercePartnerVisitFromProto).filter(Boolean),
        },
    };
}

function planInfoRestFromProto(plan) {
    if (!plan) {
        return null;
    }
    return {
        name: plan.getName?.() || '',
        subscription_price: Number(plan.getSubscriptionPrice?.() || 0),
    };
}

function subscriptionPlanSnapshotRestFromProto(plan) {
    if (!plan || (!plan.getUuid?.() && !plan.getName?.())) {
        return null;
    }
    return {
        uuid: plan.getUuid?.() || '',
        name: plan.getName?.() || '',
        subscription_price: Number(plan.getSubscriptionPrice?.() || 0),
    };
}

function subscriptionPlanOptionRestFromProto(plan) {
    if (!plan) {
        return null;
    }
    return {
        uuid: plan.getUuid?.() || '',
        name: plan.getName?.() || '',
        description: plan.getDescription?.() || '',
        monthly_billed_value: Number(plan.getMonthlyBilledValue?.() || 0),
        features: plan.getFeaturesList?.() || [],
        limits: planLimitsFromProto(plan),
    };
}

function planQuotaUsageFromProto(entry) {
    if (!entry) {
        return null;
    }
    return {
        key: entry.getKey?.() || '',
        label: entry.getLabel?.() || '',
        period: entry.getPeriod?.() || '',
        limit: Number(entry.getLimit?.() || 0),
        used: Number(entry.getUsed?.() || 0),
        unlimited: Boolean(entry.getUnlimited?.()),
        blocked: Boolean(entry.getBlocked?.()),
    };
}

export function planQuotaUsagesFromProto(entries) {
    return (entries || []).map(planQuotaUsageFromProto).filter(Boolean);
}

export function listSubscriptionPlansResponseFromProto(res) {
    return {
        success: true,
        data: (res?.getPlansList?.() || []).map(subscriptionPlanOptionRestFromProto).filter(Boolean),
    };
}

export function subscriptionPaymentRecordRestFromProto(payment) {
    if (!payment) {
        return null;
    }
    return {
        uuid: payment.getUuid?.() || '',
        subscription_plan_uuid: payment.getSubscriptionPlanUuid?.() || '',
        subscription_plan_name: payment.getSubscriptionPlanName?.() || '',
        amount_cents: Number(payment.getAmountCents?.() || 0),
        status: payment.getStatus?.() || '',
        rejection_reason: payment.getRejectionReason?.() || '',
        receipt_url: payment.getReceiptUrl?.() || '',
        charge_generated_at: timestampToISO(payment.getChargeGeneratedAt?.()),
        paid_at: timestampToISO(payment.getPaidAt?.()),
        created_at: timestampToISO(payment.getCreatedAt?.()),
        updated_at: timestampToISO(payment.getUpdatedAt?.()),
        reviewed_at: timestampToISO(payment.getReviewedAt?.()),
    };
}

function pendingSubscriptionChargeRestFromProto(charge) {
    if (!charge) {
        return null;
    }
    return {
        boleto_available: Boolean(charge.getBoletoAvailable?.()),
        pix_available: Boolean(charge.getPixAvailable?.()),
        boleto_url: charge.getBoletoUrl?.() || '',
        pix_qr_url: charge.getPixQrUrl?.() || '',
    };
}

export function mySubscriptionFromProto(data) {
    if (!data) {
        return null;
    }
    return {
        plan: planInfoRestFromProto(data.getPlan?.()),
        pending_plan: subscriptionPlanSnapshotRestFromProto(data.getPendingPlan?.()),
        pending_downgrade_plan: subscriptionPlanSnapshotRestFromProto(data.getPendingDowngradePlan?.()),
        plan_migration_pending: Boolean(data.getPlanMigrationPending?.()),
        plan_downgrade_pending: Boolean(data.getPlanDowngradePending?.()),
        plan_renewal_date: data.getPlanRenewalDate?.() || '',
        plans: (data.getPlansList?.() || []).map(subscriptionPlanOptionRestFromProto).filter(Boolean),
        latest_payment: subscriptionPaymentRecordRestFromProto(data.getLatestPayment?.()),
        payments: (data.getPaymentsList?.() || []).map(subscriptionPaymentRecordRestFromProto).filter(Boolean),
        pending_charge: pendingSubscriptionChargeRestFromProto(data.getPendingCharge?.()),
        usage: (data.getUsageList?.() || []).map(planQuotaUsageFromProto).filter(Boolean),
        mp_subscription_status: data.getMpSubscriptionStatus?.() || '',
        checkout_pending: Boolean(data.getCheckoutPending?.()),
        trial_active: Boolean(data.getTrialActive?.()),
        grace_until: data.getGraceUntil?.() || '',
        mp_preapproval_id: data.getMpPreapprovalId?.() || '',
    };
}

export function migratePlanResponseRestFromProto(res) {
    if (!res) {
        return null;
    }
    return {
        change_type: res.getChangeType?.() || '',
        user: userFromProto(res.getUser?.()),
        plan: planInfoRestFromProto(res.getPlan?.()),
        pending_plan: subscriptionPlanSnapshotRestFromProto(res.getPendingPlan?.()),
        pending_downgrade_plan: subscriptionPlanSnapshotRestFromProto(res.getPendingDowngradePlan?.()),
        plan_migration_pending: Boolean(res.getPlanMigrationPending?.()),
        plan_downgrade_pending: Boolean(res.getPlanDowngradePending?.()),
        plan_renewal_date: res.getPlanRenewalDate?.() || '',
    };
}

export function subscriptionChargeFromProto(charge) {
    if (!charge) {
        return null;
    }
    return {
        user_uuid: charge.getUserUuid?.() || '',
        user_name: charge.getUserName?.() || '',
        user_email: charge.getUserEmail?.() || '',
        charge_type: charge.getChargeType?.() || '',
        current_plan_name: charge.getCurrentPlanName?.() || '',
        billable_plan_name: charge.getBillablePlanName?.() || '',
        billable_plan_price_cents: Number(charge.getBillablePlanPriceCents?.() || 0),
        pending_plan_name: charge.getPendingPlanName?.() || '',
        pending_plan_price_cents: Number(charge.getPendingPlanPriceCents?.() || 0),
        plan_migration_pending: Boolean(charge.getPlanMigrationPending?.()),
        plan_downgrade_pending: Boolean(charge.getPlanDowngradePending?.()),
        renewal_group: charge.getRenewalGroup?.() || '',
        boleto_available: Boolean(charge.getBoletoAvailable?.()),
        pix_available: Boolean(charge.getPixAvailable?.()),
        boleto_url: charge.getBoletoUrl?.() || '',
        pix_qr_url: charge.getPixQrUrl?.() || '',
        plan_renewal_date: charge.getPlanRenewalDate?.() || '',
        updated_at: timestampToISO(charge.getUpdatedAt?.()),
    };
}

export function subscriptionChargeSectionsFromProto(sections) {
    if (!sections) {
        return { migrations: [], today: [], tomorrow: [], others: [] };
    }
    return {
        migrations: (sections.getMigrationsList?.() || []).map(subscriptionChargeFromProto).filter(Boolean),
        today: (sections.getTodayList?.() || []).map(subscriptionChargeFromProto).filter(Boolean),
        tomorrow: (sections.getTomorrowList?.() || []).map(subscriptionChargeFromProto).filter(Boolean),
        others: (sections.getOthersList?.() || []).map(subscriptionChargeFromProto).filter(Boolean),
    };
}

export function subscriptionPaymentFromProto(payment) {
    if (!payment) {
        return null;
    }
    return {
        uuid: payment.getUuid?.() || '',
        user_uuid: payment.getUserUuid?.() || '',
        user_name: payment.getUserName?.() || '',
        user_email: payment.getUserEmail?.() || '',
        subscription_plan_uuid: payment.getSubscriptionPlanUuid?.() || '',
        subscription_plan_name: payment.getSubscriptionPlanName?.() || '',
        amount_cents: Number(payment.getAmountCents?.() || 0),
        status: payment.getStatus?.() || '',
        rejection_reason: payment.getRejectionReason?.() || '',
        reviewed_by: payment.getReviewedBy?.() || '',
        receipt_url: payment.getReceiptUrl?.() || '',
        created_at: timestampToISO(payment.getCreatedAt?.()),
        updated_at: timestampToISO(payment.getUpdatedAt?.()),
        reviewed_at: timestampToISO(payment.getReviewedAt?.()),
    };
}

export function mpSubscriptionAdminFromProto(row) {
    if (!row) {
        return null;
    }
    return {
        uuid: row.getUuid?.() || '',
        user_uuid: row.getUserUuid?.() || '',
        user_name: row.getUserName?.() || '',
        user_email: row.getUserEmail?.() || '',
        plan_name: row.getPlanName?.() || '',
        mp_preapproval_id: row.getMpPreapprovalId?.() || '',
        status: row.getStatus?.() || '',
        trial_active: Boolean(row.getTrialActive?.()),
        grace_until: timestampToISO(row.getGraceUntil?.()),
        next_payment_date: timestampToISO(row.getNextPaymentDate?.()),
        last_payment_status: row.getLastPaymentStatus?.() || '',
        created_at: timestampToISO(row.getCreatedAt?.()),
        updated_at: timestampToISO(row.getUpdatedAt?.()),
    };
}
